// ============================================================
// INGESTION VALIDATOR — RAG Batch 3
// Five-level quality gate for every uploaded document
// A document is not AI-searchable until it passes all 5 levels
//
// Level 1 — EXISTENCE:   Is there readable text?
// Level 2 — OCR QUALITY: Is the extracted text coherent, or garbled OCR?
// Level 3 — CONTENT:     Can we identify the document's subject matter?
// Level 4 — COVERAGE:    Was the full document indexed?
// Level 5 — STRESS TEST: Real RAG queries run against this document (level4StressTest.js)
// ============================================================

// Minimum thresholds for each level. The three MAX_*_RATE values are all in
// "hits per 1000 characters" — the same unit detectOcrQuality() returns —
// so they can be compared directly with no unit conversion.
const THRESHOLDS = {
    MIN_WORD_COUNT: 50,               // Level 1: minimum words to be "readable"
    MAX_DIGIT_CORRUPTION_RATE: 2,     // Level 2: stray-space-in-number hits per 1000 chars
    MAX_SUBSTITUTION_RATE: 2,         // Level 2: known l/I OCR substitutions per 1000 chars
    MAX_WHITESPACE_CORRUPTION_RATE: 10, // Level 2: 3+ space runs between words, per 1000 chars (outside markdown tables)
    MAX_DETACHED_FIGURE_RUN: 8,       // Level 2: longest run of consecutive lines that are only a money figure
};

// A line that is nothing but an amount: "17,802.71", "-12.47", "97.6%",
// "$1,150.00". Requires a decimal/thousands separator or %, so bare ID numbers
// (assessor parcel lists like "0974798") never count.
const DETACHED_FIGURE_LINE = /^\s*[-(]?\$?\s?\d[\d,\s]*(?:\.\s?\d{2})?\s*%?\)?\s*$/;
const HAS_AMOUNT_SHAPE = /\d\s?[.,]\s?\d|%/;

// Detects tables that were extracted with every figure split away from its
// row label — pdf-parse on a scanned financial report prints all the labels,
// then all the figures, one per line. Word/char rates can't see this (the
// figures and labels are individually fine, and long prose pages dilute any
// rate), which is how the May 2026 manager's report passed Level 2 on
// 2026-09-24 with "Glacier Checking" and "17,802.71" twenty lines apart.
// Measured on real extractions: scanned pdf-parse reports 17-52; every clean
// LlamaParse/docling document, including a 59-line parcel-ID list, 0-2.
function longestDetachedFigureRun(text) {
    let run = 0;
    let longest = 0;
    for (const line of text.split('\n')) {
        if (!line.trim()) continue;
        if (DETACHED_FIGURE_LINE.test(line) && HAS_AMOUNT_SHAPE.test(line)) {
            run += 1;
            if (run > longest) longest = run;
        } else {
            run = 0;
        }
    }
    return longest;
}

// Words that only make sense as an OCR "I" -> "l" misread (lncome, lnsurance,
// etc). Deliberately a short, high-precision list rather than a general
// "starts with l" pattern — words that legitimately start with lowercase l
// (landscaping, late, liability) must never trigger this.
const KNOWN_I_TO_L_SUBSTITUTIONS = [
    'lncome', 'lnsurance', 'ltems', 'lnc', 'lnvoice', 'lnterest', 'lnstall',
    'lnvestment', 'lndex', 'lndividual', 'lssue', 'lssued', 'lnitial',
];

// Strips markdown-table syntax lines (`| cell | cell |` and separator rows
// like `| --- | --- |`) before whitespace-corruption checking. Clean table
// extraction pads cells with spaces for alignment — that's structural, not
// corruption, and would otherwise trigger false positives on every clean
// financial report table.
function stripMarkdownTableLines(text) {
    return text
        .split('\n')
        .filter(line => !/^\s*\|.*\|\s*$/.test(line) && !/^\s*\|?[\s:-]+\|/.test(line))
        .join('\n');
}

// Detects garbled OCR text using three independent, high-precision signals
// rather than a general "does this look like English" heuristic — word-shape
// checks turned out to pass corrupted words like "lncome" and "Equiry"
// undetected, since they're still letter strings with vowels. These signals
// target the specific artifacts OCR actually produces:
//  1. digit-run corruption — OCR frequently inserts a stray space inside a
//     number ("1 05,919.26" instead of "105,919.26"). Real text essentially
//     never does this.
//  2. known I/l substitutions — a short list of words that are only ever
//     seen as OCR misreads of a capital "I", never as legitimate lowercase-l
//     words.
//  3. whitespace corruption — some OCR/PDF-reconstruction tools emit each
//     word as a separately-positioned text object, so pdf-parse reconstructs
//     large, irregular gaps between essentially every word (confirmed on a
//     real Crestview manager's report re-OCR'd through a third-party tool:
//     "General          Liability         Insurance" instead of "General
//     Liability Insurance"). This breaks exact-phrase retrieval matching
//     even though individual words are spelled correctly. Checked outside
//     markdown table lines, where multi-space cell padding is structural.
function detectOcrQuality(text) {
    const normalized = text || '';
    const length = normalized.length || 1;

    const digitCorruptionMatches = normalized.match(/\b\d{1,2}\s\d{2,3}[,.]?\d*\b/g) || [];

    let substitutionCount = 0;
    for (const word of KNOWN_I_TO_L_SUBSTITUTIONS) {
        const matches = normalized.match(new RegExp(`\\b${word}\\b`, 'g'));
        if (matches) substitutionCount += matches.length;
    }

    const proseOnly = stripMarkdownTableLines(normalized);
    const proseLength = proseOnly.length || 1;
    // Horizontal whitespace only. `\s` also matched newlines, so blank lines
    // around page breaks in clean LlamaParse/docling output counted as
    // "irregular spacing" and failed live docs on Re-validate (2026-09-24:
    // January 2026 + November 2025 reports, June 16 2025 minutes — all 100%
    // newline matches). The corruption this targets is words spread apart on
    // one line ("General          Liability").
    const whitespaceCorruptionMatches = proseOnly.match(/\S[ \t ]{3,}\S/g) || [];

    return {
        digitCorruptionCount: digitCorruptionMatches.length,
        digitCorruptionRate: (digitCorruptionMatches.length / length) * 1000,
        substitutionCount,
        substitutionRate: (substitutionCount / length) * 1000,
        whitespaceCorruptionCount: whitespaceCorruptionMatches.length,
        whitespaceCorruptionRate: (whitespaceCorruptionMatches.length / proseLength) * 1000,
        detachedFigureRun: longestDetachedFigureRun(normalized),
    };
}

export const ingestionValidator = {

    // Run all automatic levels (1-3)
    // Returns validation report — does NOT make AI calls
    // Level 4 is manual and handled separately
    runAutomaticValidation: async (document, extractionResult) => {
        const report = {
            document_id: document.id,
            document_name: document.name,
            levels: {},
            highest_level_passed: 0,
            ready_for_level_5: false,
            timestamp: new Date().toISOString(),
        };

        // ── LEVEL 1: EXISTENCE TEST ──────────────────────────────
        const level1 = {
            level: 1,
            name: 'Existence Test',
            description: 'Does the document contain readable text?',
        };

        if (!extractionResult || !extractionResult.text) {
            level1.passed = false;
            level1.message = 'No text could be extracted from this document.';
            level1.detail = extractionResult?.note || 'Unknown extraction error.';
            report.levels[1] = level1;
            return report; // Stop here — no text means levels 2-5 impossible
        }

        const wordCount = extractionResult?.wordCount || 0;
        const hasText = !!(extractionResult?.text && wordCount > 0);

        level1.passed = hasText && wordCount >= THRESHOLDS.MIN_WORD_COUNT;
        level1.word_count = wordCount;
        level1.page_count = extractionResult?.pageCount || 0;
        level1.extraction_method = extractionResult?.method || 'unknown';
        level1.extraction_quality = extractionResult?.quality || 'failed';
        level1.message = level1.passed
            ? `✓ ${wordCount.toLocaleString()} words extracted across ${extractionResult?.pageCount || 1} page(s).`
            : hasText
                ? `✗ Only ${wordCount} words found. Minimum is ${THRESHOLDS.MIN_WORD_COUNT}.`
                : `✗ No readable text found. Document may be image-based or corrupted.`;

        report.levels[1] = level1;
        if (!level1.passed) return report;
        report.highest_level_passed = 1;

        // ── LEVEL 2: OCR QUALITY TEST ─────────────────────────────
        // Catches the case where text WAS extracted (Level 1 passes) but it's
        // garbled OCR output — e.g. a scanned document with a low-quality OCR
        // text layer already baked in by the scanner, which pdf-parse then
        // faithfully extracts as-is. Word count alone can't detect this: a
        // corrupted document can have plenty of "words" that are individually
        // letter-strings but not real content ("lncome", "1 05,919.26").
        const level2 = {
            level: 2,
            name: 'OCR Quality Test',
            description: 'Is the extracted text coherent, or garbled OCR output?',
        };

        const ocrStats = detectOcrQuality(extractionResult.text);
        const failureReasons = [];
        if (ocrStats.digitCorruptionRate > THRESHOLDS.MAX_DIGIT_CORRUPTION_RATE) {
            failureReasons.push(`${ocrStats.digitCorruptionCount} corrupted number(s) (stray spaces inside figures)`);
        }
        if (ocrStats.substitutionRate > THRESHOLDS.MAX_SUBSTITUTION_RATE) {
            failureReasons.push(`${ocrStats.substitutionCount} likely character misread(s) (e.g. "I" read as "l")`);
        }
        if (ocrStats.whitespaceCorruptionRate > THRESHOLDS.MAX_WHITESPACE_CORRUPTION_RATE) {
            failureReasons.push(`irregular spacing between ${ocrStats.whitespaceCorruptionCount} word pair(s), breaking phrase matching`);
        }
        if (ocrStats.detachedFigureRun > THRESHOLDS.MAX_DETACHED_FIGURE_RUN) {
            failureReasons.push(`table figures separated from their labels (${ocrStats.detachedFigureRun} figures in a row with no label), so amounts can't be matched to line items`);
        }

        level2.passed = failureReasons.length === 0;
        level2.digit_corruption_count = ocrStats.digitCorruptionCount;
        level2.substitution_count = ocrStats.substitutionCount;
        level2.whitespace_corruption_count = ocrStats.whitespaceCorruptionCount;
        level2.detached_figure_run = ocrStats.detachedFigureRun;
        level2.message = level2.passed
            ? '✓ Extracted text looks coherent — no OCR corruption detected.'
            : `✗ This looks like a scanned document with poor OCR text baked in ` +
              `by whatever software produced the PDF (${failureReasons.join('; ')}). ` +
              `Answers pulled from this document will likely be wrong or incomplete. ` +
              `A cleaner scan or a native digital export is needed before this can be trusted for AI search.`;

        report.levels[2] = level2;
        if (!level2.passed) return report;
        report.highest_level_passed = 2;

        // ── LEVEL 3: CONTENT TEST ────────────────────────────────
        const level3 = {
            level: 3,
            name: 'Content Test',
            description: 'Can we identify the document\'s subject matter?',
        };

        // Real content check: verify extracted text contains meaningful content
        // (full Claude-based content verification comes in Batch 5)
        const hasSubstantialContent = (extractionResult?.text || '').length > 200;
        const firstChunk = (extractionResult?.text || '').substring(0, 1000);
        const uniqueWords = new Set(
            firstChunk.toLowerCase().match(/\b[a-z]{4,}\b/g) || []
        ).size;

        level3.passed = hasSubstantialContent && uniqueWords > 12;
        level3.score = Math.min(100, uniqueWords * 2);
        level3.message = level3.passed
            ? `✓ Document content verified (${uniqueWords} unique terms found).`
            : `✗ Insufficient content variety. Document may be corrupted ` +
            `or mostly non-text.`;

        report.levels[3] = level3;
        if (!level3.passed) return report;
        report.highest_level_passed = 3;

        // ── LEVEL 4: COVERAGE TEST ───────────────────────────────
        const level4 = {
            level: 4,
            name: 'Coverage Test',
            description: 'Was the complete document indexed, not just the first pages?',
        };

        // Real coverage check: verify content exists beyond first 25% of text
        const fullText = extractionResult?.text || '';
        const cutPoint = Math.floor(fullText.length * 0.75);
        const laterContent = fullText.substring(cutPoint).trim();
        const hasLaterContent = laterContent.length > 100;

        level4.passed = hasLaterContent;
        level4.message = hasLaterContent
            ? `✓ Full document coverage confirmed — ` +
            `content found throughout all sections.`
            : `✗ Document appears truncated. ` +
            `Later sections may not have been indexed.`;

        report.levels[4] = level4;
        if (!level4.passed) return report;
        report.highest_level_passed = 4;
        report.ready_for_level_5 = true;

        return report;
    },

    // Get validation status summary for display
    getStatusSummary: (document) => {
        const statusMap = {
            pending: { label: 'Awaiting validation', color: 'amber', level: 0 },
            validating: { label: 'Validating...', color: 'blue', level: 0 },
            live: { label: 'AI searchable', color: 'green', level: 4 },
            failed: { label: 'Validation failed', color: 'red', level: 0 },
            superseded: { label: 'Superseded', color: 'slate', level: 0 },
            excluded: { label: 'Excluded from AI', color: 'slate', level: 0 },
        };
        return statusMap[document.ai_status] ||
            { label: 'Unknown', color: 'slate', level: 0 };
    },
};
